import { useState, useMemo } from 'react';
import { Accordion, Alert, AlertDescription, Button } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import type { SchemaWidget } from '@/shared/ui/SchemaForm';
import { WorkspaceConfigRow } from './WorkspaceConfigRow';
import { AddWorkspaceConfig } from './AddWorkspaceConfig';
import { Trans, useTranslation } from 'react-i18next';

interface WorkspaceScopeTabProps {
  toolId: string;
  jsonSchema: Record<string, unknown>;
  configs: Array<{
    id: string;
    workspaceId?: string;
    data: Record<string, unknown>;
  }>;
  allWorkspaces: WorkspaceResponseDto[];
  fullyConfiguredWorkspaceIds?: string[];
  description: string;
  fallbackLabel: string;
  onSave: (workspaceId: string, data: Record<string, unknown>) => void | Promise<void>;
  onRemove: (workspaceId: string) => void | Promise<void>;
  onBulkSave: (workspaceIds: string[], data: Record<string, unknown>) => void | Promise<void>;
  isSaving: boolean;
  isRemoving: boolean;
  isBulkSaving: boolean;
  uiSchemaOverride?: Record<string, unknown>;
  /** RJSF widget for `kbSelect`. Injected by the page (dependency injection). */
  kbSelectWidget?: SchemaWidget;
  /** RJSF widget for `kbMultiSelect`. Injected by the page (dependency injection). */
  kbMultiSelectWidget?: SchemaWidget;
}

export function WorkspaceScopeTab({
  toolId,
  jsonSchema,
  configs,
  allWorkspaces,
  fullyConfiguredWorkspaceIds = [],
  description,
  fallbackLabel,
  onSave,
  onRemove,
  onBulkSave,
  isSaving,
  isRemoving,
  isBulkSaving,
  uiSchemaOverride,
  kbSelectWidget,
  kbMultiSelectWidget,
}: WorkspaceScopeTabProps) {
  const { t } = useTranslation('admin');

  const [isAdding, setIsAdding] = useState(false);
  const [openWorkspaceId, setOpenWorkspaceId] = useState<string | undefined>(undefined);

  // Get workspace names for configured workspaces
  const configuredWorkspaceIds = new Set(
    configs.map((c) => c.workspaceId).filter((id): id is string => id !== undefined),
  );

  const availableWorkspaces = allWorkspaces.filter((ws) => !configuredWorkspaceIds.has(ws.id));

  const handleBulkSave = async (workspaceIds: string[], data: Record<string, unknown>) => {
    await onBulkSave(workspaceIds, data);
    setIsAdding(false);
  };

  const getWorkspace = (workspaceId: string | undefined) => {
    // eslint-disable-next-line i18next/no-literal-string -- unreachable: the caller only maps rows already filtered to a defined workspaceId
    if (!workspaceId) return { name: 'Unknown', logoUrl: null };
    const workspace = allWorkspaces.find((ws) => ws.id === workspaceId);
    return { name: workspace?.name ?? workspaceId, logoUrl: workspace?.logoUrl ?? null };
  };

  const hasConfigs = configs.length > 0;

  const hasPartialWorkspaces = useMemo(() => {
    if (!hasConfigs) return false;
    return configs.some(
      (c) => c.workspaceId && !fullyConfiguredWorkspaceIds.includes(c.workspaceId),
    );
  }, [configs, fullyConfiguredWorkspaceIds, hasConfigs]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-row items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">{description}</p>
        {hasConfigs && !isAdding && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsAdding(true)}
            disabled={availableWorkspaces.length === 0}
          >
            <PlusIcon aria-hidden="true" className="size-4" />
            {t('admin.workspaceScopeTab.addWorkspaces')}
          </Button>
        )}
      </div>

      {hasPartialWorkspaces && !isAdding && (
        <Alert className="border-warning bg-warning/10">
          <AlertDescription>
            <Trans
              i18nKey="admin.workspaceScopeTab.partiallyConfiguredWarning"
              ns="admin"
              components={{ save: <strong />, advanced: <strong /> }}
            />
          </AlertDescription>
        </Alert>
      )}

      {/* Add workspace config section */}
      {isAdding && (
        <AddWorkspaceConfig
          toolId={toolId}
          jsonSchema={jsonSchema}
          availableWorkspaces={availableWorkspaces}
          onSave={handleBulkSave}
          onCancel={() => setIsAdding(false)}
          isSaving={isBulkSaving}
          uiSchemaOverride={uiSchemaOverride}
          kbSelectWidget={kbSelectWidget}
          kbMultiSelectWidget={kbMultiSelectWidget}
        />
      )}

      {/* Configured workspaces list. One accordion owns the whole list rather than each row
          collapsing itself: only one workspace is open at a time, and arrow keys move between
          them. Controlled so a row can close itself once its save lands. */}
      {hasConfigs && (
        <Accordion
          type="single"
          collapsible
          value={openWorkspaceId}
          onValueChange={setOpenWorkspaceId}
          className={`flex flex-col gap-xs ${isAdding ? 'pointer-events-none opacity-50' : ''}`}
        >
          {configs
            .filter((config) => config.workspaceId !== undefined)
            .map((config) => {
              const ws = getWorkspace(config.workspaceId);
              return (
                <WorkspaceConfigRow
                  key={config.id}
                  config={config}
                  workspaceName={ws.name}
                  workspaceLogoUrl={ws.logoUrl}
                  toolId={toolId}
                  jsonSchema={jsonSchema}
                  onSave={(data) => onSave(config.workspaceId as string, data)}
                  onRemove={() => onRemove(config.workspaceId as string)}
                  isSaving={isSaving}
                  isRemoving={isRemoving}
                  onSaved={() => setOpenWorkspaceId(undefined)}
                  isPartial={
                    !!config.workspaceId &&
                    !fullyConfiguredWorkspaceIds.includes(config.workspaceId)
                  }
                  uiSchemaOverride={uiSchemaOverride}
                  kbSelectWidget={kbSelectWidget}
                  kbMultiSelectWidget={kbMultiSelectWidget}
                />
              );
            })}
        </Accordion>
      )}

      {!hasConfigs && !isAdding && (
        <div className="flex flex-col items-center justify-center gap-2 p-16 text-center">
          <p className="text-base text-muted-foreground">
            {t('admin.workspaceScopeTab.noWorkspaceSpecificConfigurations')}
          </p>
          <p className="mb-4 text-sm text-muted-foreground">{fallbackLabel}</p>
          <Button
            type="button"
            onClick={() => setIsAdding(true)}
            disabled={availableWorkspaces.length === 0}
          >
            <PlusIcon aria-hidden="true" className="size-4" />
            {t('admin.workspaceScopeTab.addWorkspaceConfiguration')}
          </Button>
        </div>
      )}

      {hasConfigs && (
        <div className="border-t border-border pt-4">
          <p className="text-xs text-muted-foreground">
            {t('admin.workspaceScopeTab.customConfigurationSummary', {
              context: configs.length === 1 ? 'singular' : 'plural',
              count: allWorkspaces.length,
              configured: configs.length,
            })}
          </p>
        </div>
      )}
    </div>
  );
}
